import { useState, useEffect, useRef } from 'react';
import { CinematicReveal } from './CinematicReveal';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { useReportTheme } from '../../contexts/ReportThemeContext';
import { useTranslate } from '../../hooks/useTranslate';
import { DataObjectDescription } from '../../types/report';
import { BulletPointText } from '../common/BulletPointText';

interface Issue {
    reason: string;
    severity: string;
    confidence: string;
    issue_type: string;
    description: string;
    remediation: {
        action_plan: string[];
        solution_strategy: string;
    };
    flag_for_deep: boolean;
    business_impact: string;
    brief_description: string;
    root_cause_analysis: string;
}

interface ProblematicColumn {
    issues: Issue[];
    column_name: string;
    business_criticality: string;
}

interface ReasoningResult {
    problematic_columns: ProblematicColumn[];
}

interface CinematicExecutiveSummaryProps {
    summary?: {
        headline: string;
        keyFindings: string[];
        nextSteps?: string[];
        recommendedNextAction?: string;
    };
    dataObjectDescription?: DataObjectDescription;
    executiveSummary?: {
        [key: string]: string;
    };
    reasoningResult?: ReasoningResult;
    datasetName?: string;
}

export function CinematicExecutiveSummary({
    summary,
    dataObjectDescription,
    executiveSummary,
    datasetName
}: CinematicExecutiveSummaryProps) {
    const reduced = useReducedMotion();
    const { getThemeClasses } = useReportTheme();
    const themeClasses = getThemeClasses();
    const { t } = useTranslate();



    // Internal staged animations driven once contentVisible fires
    const [staged, setStaged] = useState({ card: false, metrics: false, summary: false, findings: false });
    const [nextStepsVisible, setNextStepsVisible] = useState(false);
    const [showExtraSummary, setShowExtraSummary] = useState(false);
    const nextStepsRef = useRef<HTMLDivElement>(null);



    // Separate intersection observer for Next Steps section
    useEffect(() => {
        const element = nextStepsRef.current;
        if (!element) {
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !nextStepsVisible) {
                    setNextStepsVisible(true);
                }
            },
            {
                threshold: 0.1, // Lowered threshold to make it easier to trigger
                rootMargin: '0px 0px -5% 0px' // Reduced margin
            }
        );

        observer.observe(element);
        return () => {
            observer.disconnect();
        };
    }, [nextStepsVisible]);

    const runStaged = () => {
        if (staged.card) return; // already ran
        if (reduced) {
            setStaged({ card: true, metrics: true, summary: true, findings: true });
            return;
        }
        const t1 = setTimeout(() => setStaged(s => ({ ...s, card: true })), 40);
        const t2 = setTimeout(() => setStaged(s => ({ ...s, metrics: true })), 260);
        const t3 = setTimeout(() => setStaged(s => ({ ...s, summary: true })), 480);
        const t4 = setTimeout(() => setStaged(s => ({ ...s, findings: true })), 760);
        return () => { [t1, t2, t3, t4].forEach(clearTimeout); };
    };

    // Reset animations when component unmounts
    useEffect(() => {
        return () => {
            setStaged({ card: false, metrics: false, summary: false, findings: false });
        };
    }, []);

    // Fallback timer for Next Steps animation (in case intersection observer fails)
    useEffect(() => {
        const fallbackTimer = setTimeout(() => {
            if (!nextStepsVisible) {
                setNextStepsVisible(true);
            }
        }, 3000); // 3 second fallback

        return () => clearTimeout(fallbackTimer);
    }, [nextStepsVisible]);

    return (
        <CinematicReveal headerDelay={0} contentDelay={200}>
            {({ ref, headerStyle, contentStyle: _contentStyle, contentVisible }) => {
                if (contentVisible) runStaged();
                return (
                    <section
                        ref={ref}
                        id="hero"
                        data-legacy-id="data-report"
                        className={`relative min-h-screen flex items-center justify-center overflow-hidden ${themeClasses.sectionBackground}`}
                        role="region"
                        aria-labelledby="hero-heading"
                    >
                        {/* Background Pattern */}
                        <div
                            className="absolute inset-0 opacity-20"
                            style={{
                                backgroundImage: `
                        radial-gradient(circle at 25% 25%, rgba(255,255,255,0.15) 0%, transparent 50%),
                        radial-gradient(circle at 75% 75%, rgba(255,255,255,0.15) 0%, transparent 50%)
                    `
                            }}
                        />

                        {/* Removed outer centered container per design update */}
                        <div className="mt-20 pt-8" style={headerStyle}>
                            {/* Data Object Description - Title Outside, Content Inside Card */}
                            {dataObjectDescription && (
                                <>
                                    {/* Title Outside Card */}
                                    <div className="text-center mb-6 max-w-6xl mx-auto">
                                        <h2 className={`${themeClasses.headingText}`} style={{ fontSize: '20px' }}>
                                            {(() => {
                                                // Check if datasetName contains file extensions (upload model)
                                                const isUploadedFile = datasetName && /\.(csv|xlsx?|json|txt)$/i.test(datasetName);

                                                if (isUploadedFile) {
                                                    return t('Data Analysis Report');
                                                } else if (datasetName) {
                                                    // Databricks or other data sources - show title and name on separate lines
                                                    return (
                                                        <>
                                                            <div>{t('Data Analysis Report')}</div>
                                                            <div className={`notranslate ${themeClasses.headingText}`} translate="no" style={{ fontSize: '18px', marginTop: '8px', opacity: 0.8 }}>{datasetName}</div>
                                                        </>
                                                    );
                                                } else {
                                                    return t('Data Analysis Report');
                                                }
                                            })()}
                                        </h2>
                                    </div>

                                    {/* Content Inside Card */}
                                    <div
                                        className={`backdrop-blur-md rounded-xl p-8 border ${themeClasses.cardBorder} mb-8 max-w-6xl mx-auto ${themeClasses.cardBackground}`}
                                        style={reduced ? {} : {
                                            opacity: staged.card ? 1 : 0,
                                            transition: 'opacity 0.5s ease-out',
                                        }}
                                    >
                                        {/* Description Inside Card */}
                                        <div className="text-left mb-6">
                                            <div className={`${themeClasses.bodyText} max-w-4xl mx-auto`} style={{ fontSize: '15px', lineHeight: '1.6' }}>
                                                <p className="leading-relaxed">
                                                    <span className="notranslate" translate="no">{dataObjectDescription.description || 'No description available'}</span>
                                                </p>
                                            </div>
                                        </div>                                        {/* Dataset Overview Metrics */}
                                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-8">
                                            <div className={`text-center p-4 rounded-xl border ${themeClasses.cardBorder} ${themeClasses.metricCardGradient}`}>
                                                <div className="text-sm uppercase tracking-wider mb-2" style={{ color: '#D31145' }}>
                                                    {t('Total Rows')}
                                                </div>
                                                <div className="text-2xl font-bold text-black notranslate" translate="no">
                                                    {(dataObjectDescription.total_rows || 0).toLocaleString()}
                                                </div>
                                            </div>
                                            <div className={`text-center p-4 rounded-xl border ${themeClasses.cardBorder} ${themeClasses.metricCardGradient}`}>
                                                <div className="text-sm uppercase tracking-wider mb-2" style={{ color: '#D31145' }}>
                                                    {t('Total Columns')}
                                                </div>
                                                <div className="text-2xl font-bold text-black notranslate" translate="no">
                                                    {dataObjectDescription.total_columns || 0}
                                                </div>
                                            </div>
                                            <div className={`text-center p-4 rounded-xl border ${themeClasses.cardBorder} ${themeClasses.metricCardGradient}`}>
                                                <div className="text-sm uppercase tracking-wider mb-2" style={{ color: '#D31145' }}>
                                                    {t('Data Types')}
                                                </div>
                                                <div className="text-lg font-bold text-black space-y-1 notranslate" translate="no">
                                                    {/* Column types - filter out types with count 0 */}
                                                    {dataObjectDescription.column_types ? (
                                                        dataObjectDescription.column_types.split(',')
                                                            .map(type => type.trim())
                                                            .filter(type => {
                                                                // Extract count from "type: count" format
                                                                const match = type.match(/:\s*(\d+)$/);
                                                                return match && parseInt(match[1]) > 0;
                                                            })
                                                            .map((type, index) => (
                                                                <div key={index} className="leading-tight">
                                                                    {type}
                                                                </div>
                                                            ))
                                                    ) : (
                                                        <div className="leading-tight text-black">No data</div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className={`text-center p-4 rounded-xl border ${themeClasses.cardBorder} ${themeClasses.metricCardGradient}`}>
                                                <div className="text-sm uppercase tracking-wider mb-2" style={{ color: '#D31145' }}>
                                                    {t('Table Type')}
                                                </div>
                                                <div className="text-2xl font-bold text-black notranslate" translate="no">
                                                    {dataObjectDescription.table_type && dataObjectDescription.table_type.toLowerCase() !== 'unknown' ? dataObjectDescription.table_type : 'N/A'}
                                                </div>
                                            </div>
                                            <div className={`text-center p-4 rounded-xl border ${themeClasses.cardBorder} ${themeClasses.metricCardGradient}`}>
                                                <div className="text-sm uppercase tracking-wider mb-2" style={{ color: '#D31145' }}>
                                                    {t('Issues Diagnosed')}
                                                </div>
                                                <div className="text-2xl font-bold text-black notranslate" translate="no">
                                                    {dataObjectDescription.total_diagnosis_problems || 0}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Data Hygiene Section */}
                            {executiveSummary && (
                                <>
                                    {/* Data Hygiene Title Outside Card */}
                                    <div className="text-center mb-6 max-w-6xl mx-auto">
                                        <h3 className="font-bold text-black" style={{ fontSize: '30px' }}>
                                            {t('Data Hygiene')}
                                        </h3>
                                    </div>

                                    {/* Data Hygiene Content Inside Card */}
                                    <div
                                        className={`backdrop-blur-md rounded-xl p-8 border ${themeClasses.cardBorder} max-w-6xl mx-auto ${themeClasses.cardBackground}`}
                                        style={reduced ? {} : {
                                            opacity: staged.findings ? 1 : 0,
                                            transition: 'opacity 0.5s ease-out',
                                        }}
                                    >
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            {/* Dynamic Executive Summary Sections */}
                                            {executiveSummary && Object.entries(executiveSummary).filter(([key]) => key !== 'next_steps' && key !== 'dataset_overview' && key !== 'extra_summary').map(([key, value]) => {
                                                // Convert snake_case keys to readable headers
                                                const header = key.split('_').map(word =>
                                                    word.charAt(0).toUpperCase() + word.slice(1)
                                                ).join(' ');

                                                return (
                                                    <div
                                                        key={key}
                                                        className={`rounded-xl p-6 border ${themeClasses.cardBorder} bg-white/80`}
                                                    >
                                                        <h4 className="font-bold text-center mb-4" style={{ fontSize: '18px', color: '#D31145' }}>
                                                            {t(header)}
                                                        </h4>
                                                        <div className="text-sm leading-relaxed" style={{ fontSize: '14px', lineHeight: '1.7' }}>
                                                            <BulletPointText
                                                                text={value || `No ${header.toLowerCase()} information available.`}
                                                                className="text-gray-900"
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                            {/* Extra Summary Points as Cards - Conditionally Rendered */}
                                            {showExtraSummary && executiveSummary?.extra_summary && (() => {
                                                // Parse numbered points from extra_summary text
                                                const points = executiveSummary.extra_summary
                                                    .split(/\d+\.\s+/) // Split by number followed by dot and space
                                                    .filter(point => point.trim()) // Remove empty entries
                                                    .map(point => point.replace(/\s+\d+\.\s+.*$/, '').trim()); // Clean up any trailing numbered items

                                                return points.map((point, index) => {
                                                    // Extract the header from the point (text before the colon)
                                                    const [headerText, ...rest] = point.split(':');
                                                    const content = rest.join(':').trim();

                                                    return (
                                                        <div
                                                            key={`extra-${index}`}
                                                            className={`rounded-xl p-6 border ${themeClasses.cardBorder} bg-white/80`}
                                                        >
                                                            <h4 className="font-bold text-center mb-4" style={{ fontSize: '18px', color: '#D31145' }}>
                                                                {headerText.trim()}
                                                            </h4>
                                                            <div className="text-sm leading-relaxed" style={{ fontSize: '14px', lineHeight: '1.7' }}>
                                                                <BulletPointText
                                                                    text={content || point}
                                                                    className="text-gray-900"
                                                                />
                                                            </div>
                                                        </div>
                                                    );
                                                });
                                            })()}
                                        </div>

                                        {/* View More Button for Extra Summary */}
                                        {executiveSummary?.extra_summary && (
                                            <div className="text-center mt-6">
                                                <button
                                                    onClick={() => setShowExtraSummary(!showExtraSummary)}
                                                    className={`px-6 py-3 rounded-xl border ${themeClasses.cardBorder} bg-white/80 transition-shadow duration-200 hover:shadow-md font-medium`}
                                                    style={{ fontSize: '14px' }}
                                                >
                                                    <span className="text-black font-semibold">
                                                        {showExtraSummary ? `▲ ${t('View Less')}` : `▼ ${t('View More')}`}
                                                    </span>
                                                </button>
                                            </div>
                                        )}

                                        {/* Next Steps Section - Inside Key Findings */}
                                        {executiveSummary?.next_steps && (
                                            <div className="mt-8">
                                                <div className={`rounded-xl p-8 border ${themeClasses.cardBorder} bg-white/80`}
                                                    style={reduced ? {} : {
                                                        opacity: staged.findings ? 1 : 0,
                                                        transition: 'opacity 0.5s ease-out',
                                                    }}
                                                >
                                                    <h4 className="font-bold text-center mb-6" style={{ fontSize: '22px', color: '#D31145' }}>
                                                        {t('Next Steps')}
                                                    </h4>
                                                    <div className="leading-relaxed" style={{ fontSize: '15px', lineHeight: '1.7' }}>
                                                        <div className="space-y-6">
                                                            {(() => {
                                                                // Parse and group next steps by category
                                                                const fullText = executiveSummary.next_steps
                                                                    .replace(/[\u{1F600}-\u{1F9FF}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1FA00}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{FE00}-\u{FE0F}\u{200D}\u{20E3}\u{E0020}-\u{E007F}]/gu, '').replace(/  +/g, ' ');
                                                                const categories: Record<string, string[]> = {};

                                                                // Split on real newlines first – preserves backend structure
                                                                const lines = fullText.split(/\n/).map(l => l.trim()).filter(l => l.length > 0);

                                                                // Generic category header detection:
                                                                // A line that is "Some Label:" or "Some Label: short text" (≤60 char label, 1-6 words, starts uppercase)
                                                                // Also matches lines starting with bullet/number markers followed by a header pattern
                                                                const isCategoryHeader = (line: string): { name: string; afterContent: string } | null => {
                                                                    // Strip leading bullet/number markers first
                                                                    const stripped = line
                                                                        .replace(/^[-•*‣✓✔▸►→]\s*/, '')
                                                                        .replace(/^\d+[.)]\s+/, '')
                                                                        .replace(/^\([a-z]\)\s+/i, '')
                                                                        .replace(/^\*\*(.+?)\*\*:?\s*/, '$1: ')  // **Bold Header**: → Header:
                                                                        .trim();

                                                                    // Match "Label:" with optional content after colon
                                                                    const match = stripped.match(/^([A-Z][A-Za-z0-9 &/,'()-]{1,58}):\s*(.*)/);
                                                                    if (!match) return null;

                                                                    const label = match[1].trim();
                                                                    const after = match[2].trim();
                                                                    const wordCount = label.split(/\s+/).length;

                                                                    // Must be 1-8 words to qualify as a header (not a full sentence)
                                                                    if (wordCount > 8) return null;

                                                                    // If there's content after the colon, it should be short enough
                                                                    // that this still looks like "Header: brief item" not "Sentence: continuation"
                                                                    // But we allow any length — the after-content becomes the first bullet
                                                                    return { name: label, afterContent: after };
                                                                };

                                                                let currentCategory = 'General';
                                                                const categoryOrder: string[] = ['General'];

                                                                // Helper: split inline numbered items "1) item 2) item" into separate entries
                                                                const splitInlineNumbers = (text: string): string[] => {
                                                                    const hits = text.match(/\d{1,3}\)\s+/g);
                                                                    if (hits && hits.length >= 2) {
                                                                        return text.split(/\s+(?=\d{1,3}\)\s+)/)
                                                                            .map(p => p.replace(/^\d+\)\s+/, '').replace(/\.$/, '').trim())
                                                                            .filter(p => p.length > 3);
                                                                    }
                                                                    return [text.replace(/\.$/, '').trim()].filter(p => p.length > 3);
                                                                };

                                                                for (const line of lines) {
                                                                    const headerMatch = isCategoryHeader(line);

                                                                    if (headerMatch) {
                                                                        currentCategory = headerMatch.name;
                                                                        if (!categoryOrder.includes(currentCategory)) {
                                                                            categoryOrder.push(currentCategory);
                                                                        }

                                                                        // If there's content after the colon, split inline numbered items
                                                                        if (headerMatch.afterContent.length > 5) {
                                                                            if (!categories[currentCategory]) categories[currentCategory] = [];
                                                                            const items = splitInlineNumbers(headerMatch.afterContent);
                                                                            categories[currentCategory].push(...items);
                                                                        }
                                                                        continue;
                                                                    }

                                                                    if (!categories[currentCategory]) categories[currentCategory] = [];

                                                                    // Split inline numbered items, then strip markers
                                                                    const expandedItems = splitInlineNumbers(
                                                                        line
                                                                            .replace(/^[-•*]\s+/, '')         // bullet markers
                                                                            .replace(/^\([a-z]\)\s+/i, '')    // lettered markers
                                                                            .trim()
                                                                    );

                                                                    for (const item of expandedItems) {
                                                                        const cleaned = item
                                                                            .replace(/\.$/, '')               // trailing period
                                                                            .replace(/\s*-\s*$/, '')          // trailing dash
                                                                            .trim();
                                                                        if (cleaned.length > 3) {
                                                                            categories[currentCategory].push(cleaned);
                                                                        }
                                                                    }
                                                                }

                                                                // Render grouped categories in discovery order
                                                                return categoryOrder.map(categoryName => {
                                                                    const items = categories[categoryName];
                                                                    if (!items || items.length === 0) return null;

                                                                    return (
                                                                        <div key={categoryName} className="space-y-3">
                                                                            {/* Category Header - only show if not General */}
                                                                            {categoryName !== 'General' && (
                                                                                <h5 className="font-bold text-lg" style={{ color: '#D31145' }}>
                                                                                    {categoryName}
                                                                                </h5>
                                                                            )}

                                                                            {/* Category Items */}
                                                                            <div className={`space-y-2 ${categoryName !== 'General' ? 'ml-4' : ''}`}>
                                                                                {items.map((item, index) => {
                                                                                    // Detect "Header: body" pattern for bold rendering
                                                                                    const headerMatch = item.match(/^([A-Z][A-Za-z0-9 &/,'()%-]{1,58}):\s+(.+)/);
                                                                                    const isHeaderColon = headerMatch && headerMatch[1].trim().split(/\s+/).length <= 6;
                                                                                    return (
                                                                                        <div key={index} className="flex items-start gap-3">
                                                                                            <span
                                                                                                className={`${themeClasses.accentText} text-lg font-bold flex-shrink-0 mt-0.5`}
                                                                                                style={{ fontSize: '18px' }}
                                                                                            >
                                                                                                •
                                                                                            </span>
                                                                                            <p className="mb-0 flex-1 text-gray-900 leading-relaxed notranslate" translate="no">
                                                                                                {isHeaderColon ? (
                                                                                                    <>
                                                                                                        <span className="font-bold text-gray-900">{headerMatch![1]}:</span>{' '}
                                                                                                        <span>{headerMatch![2]}</span>
                                                                                                    </>
                                                                                                ) : item}
                                                                                            </p>
                                                                                        </div>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                });
                                                            })()}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}

                            {/* Recommended Next Action Section */}
                            {summary && summary.recommendedNextAction && (
                                <div className="text-center">
                                    <h4 className={`font-semibold ${themeClasses.headingText} mb-4`} style={{ fontSize: '20px' }}>
                                        {t('Recommended Next Action')}
                                    </h4>
                                    <div className={`rounded-xl p-6 border ${themeClasses.cardBorder} ${themeClasses.cardBackground}`}>
                                        <p className={`${themeClasses.bodyText} leading-relaxed notranslate`} translate="no" style={{ fontSize: '15px', lineHeight: '1.6' }}>
                                            {summary && summary.recommendedNextAction}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Diagnostic & Prescription intentionally rendered only once via CinematicProblemSolutions component */}

                        </div>
                    </section>
                );
            }}
        </CinematicReveal>
    );
}

